<template>
  <div class="min-h-screen bg-[#f6f8fb] text-[#242e49]">
    <aside
      class="fixed inset-y-0 left-0 z-40 hidden flex-col border-r border-white/10 bg-[#162538] shadow-[0_8px_24px_rgba(15,23,42,0.14)] transition-[width] duration-200 lg:flex"
      :class="collapsed ? 'w-[72px]' : 'w-[260px]'"
    >
      <div class="flex h-20 shrink-0 items-center border-b border-white/10 px-5" :class="collapsed ? 'justify-center px-0' : 'justify-between'">
        <router-link to="/doctor/passes" class="overflow-hidden text-white">
          <span v-if="!collapsed" class="block text-[28px] font-medium leading-none tracking-[0.04em]">ARQAU</span>
          <svg v-else width="32" height="32" viewBox="0 0 32 32" fill="none"><path d="M5.3 16H13a3 3 0 013 3v7.7M26.7 16H19a3 3 0 01-3-3V5.3" stroke="white" stroke-width="1.5"/></svg>
        </router-link>
        <div v-if="!collapsed" class="flex rounded-full border border-white/10 bg-white/10 p-0.5 text-[10px] font-semibold">
          <button v-for="item in languages" :key="item.value" type="button" class="rounded-full px-2 py-1 transition" :class="lang === item.value ? 'bg-[#0066ff] text-white' : 'text-white/60 hover:text-white'" @click="switchLang(item.value)">{{ item.short }}</button>
        </div>
      </div>

      <button
        type="button"
        class="absolute -right-3.5 top-[94px] flex h-7 w-7 items-center justify-center rounded-full border border-white/20 bg-[#162538] text-white shadow-lg transition hover:bg-[#1d3046]"
        :aria-label="collapsed ? t('Развернуть меню') : t('Свернуть меню')"
        @click="toggleSidebar"
      >
        <svg class="h-4 w-4 transition-transform" :class="{ 'rotate-180': collapsed }" viewBox="0 0 16 16" fill="none"><path d="M10 12L6 8l4-4" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </button>

      <nav class="flex flex-1 flex-col gap-2 overflow-hidden px-4 py-6">
        <router-link to="/doctor/passes" class="sidebar-link" :class="[{ active: isPassesActive }, collapsed ? 'justify-center px-0' : '']" :title="collapsed ? t('Прохождения') : ''">
          <svg class="h-5 w-5 shrink-0" viewBox="0 0 20 20" fill="none"><path d="M11.7 9.2h-5M8.3 12.5H6.7m6.6-6.7H6.7m10-.1v8.6c0 1.4 0 2.1-.3 2.7-.2.4-.6.8-1.1 1.1-.5.2-1.2.2-2.6.2H7.3c-1.4 0-2.1 0-2.6-.2-.5-.3-.9-.7-1.1-1.1-.3-.6-.3-1.3-.3-2.7V5.7c0-1.4 0-2.1.3-2.7.2-.4.6-.8 1.1-1.1.5-.2 1.2-.2 2.6-.2h5.4c1.4 0 2.1 0 2.6.2.5.3.9.7 1.1 1.1.3.6.3 1.3.3 2.7z" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/></svg>
          <span v-if="!collapsed" class="truncate">{{ t('Прохождения') }}</span>
        </router-link>
        <router-link to="/doctor/history" class="sidebar-link" :class="[{ active: isHistoryActive }, collapsed ? 'justify-center px-0' : '']" :title="collapsed ? t('История') : ''">
          <svg class="h-5 w-5 shrink-0" viewBox="0 0 20 20" fill="none"><path d="M10 5.8V10l3 1.7m4.5-1.7A7.5 7.5 0 112.5 10a7.5 7.5 0 0115 0z" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/></svg>
          <span v-if="!collapsed" class="truncate">{{ t('История') }}</span>
        </router-link>
      </nav>

      <div class="shrink-0 px-4 pb-5">
        <div class="mb-2 border-t border-white/10 pt-4">
          <div class="flex items-center rounded-2xl py-2 text-white" :class="collapsed ? 'justify-center' : 'gap-3 px-1'">
            <div class="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#23354f] text-sm font-semibold ring-2 ring-white/10">
              {{ initials }}
              <span class="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-[#162538] bg-[#34d399]"></span>
            </div>
            <div v-if="!collapsed" class="min-w-0">
              <p class="truncate text-sm font-medium">{{ doctorName }}</p>
              <p class="mt-0.5 truncate text-[11px] text-white/60">{{ doctor?.specialization || t('Врач') }}</p>
              <p v-if="doctor?.clinic_name" class="mt-0.5 truncate text-[10px] text-white/40">{{ doctor.clinic_name }}</p>
            </div>
          </div>
        </div>
        <button type="button" class="sidebar-link w-full hover:bg-red-500/20" :class="collapsed ? 'justify-center px-0' : ''" :title="collapsed ? t('Выйти') : ''" @click="logout">
          <svg class="h-5 w-5 shrink-0" viewBox="0 0 20 20" fill="none"><path d="M2.1 10.4h10.8m0 0l-4.2 4.2m4.2-4.2L8.7 6.2M17.5 16.7V3.3" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>
          <span v-if="!collapsed">{{ t('Выйти') }}</span>
        </button>
      </div>
    </aside>

    <header class="sticky top-0 z-30 flex h-[68px] items-center justify-between border-b border-[#dae4f0] bg-[#fcfeff] px-5 lg:hidden">
      <router-link to="/doctor/passes" class="text-xl font-semibold tracking-[0.08em] text-[#162538]">ARQAU</router-link>
      <div class="flex items-center gap-2">
        <button type="button" class="rounded-lg border border-[#dae4f0] px-2.5 py-2 text-xs font-semibold text-[#4d5c6d]" @click="switchLang(lang === 'ru' ? 'kz' : 'ru')">{{ lang === 'ru' ? 'ҚАЗ' : 'RU' }}</button>
        <button type="button" class="rounded-lg border border-[#dae4f0] p-2 text-[#4d5c6d]" :aria-label="t('Открыть меню')" @click="mobileMenuOpen = !mobileMenuOpen">
        <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M4 7h16M4 12h16M4 17h16" stroke-width="1.8" stroke-linecap="round"/></svg>
        </button>
      </div>
    </header>

    <div v-if="mobileMenuOpen" class="fixed inset-x-4 top-[76px] z-50 rounded-2xl bg-[#162538] p-3 text-white shadow-2xl lg:hidden">
      <router-link to="/doctor/passes" class="mobile-link" :class="{ active: isPassesActive }" @click="mobileMenuOpen = false">{{ t('Прохождения') }}</router-link>
      <router-link to="/doctor/history" class="mobile-link" :class="{ active: isHistoryActive }" @click="mobileMenuOpen = false">{{ t('История') }}</router-link>
      <button type="button" class="mobile-link w-full text-left text-red-200" @click="logout">{{ t('Выйти') }}</button>
    </div>

    <main class="min-h-screen transition-[margin] duration-200" :style="{ marginLeft: desktopMargin }">
      <router-view />
    </main>
  </div>
</template>

<script>
import api from '@/services/api'
import { t, setLang, currentLang } from '@/i18n'

export default {
  name: 'DoctorLayout',
  data() {
    return {
      user: null,
      collapsed: localStorage.getItem('qamqor_doctor_sidebar_collapsed') === 'true',
      mobileMenuOpen: false,
      desktop: window.innerWidth >= 1024,
      languages: [{ value: 'ru', short: 'RU' }, { value: 'kz', short: 'ҚАЗ' }]
    }
  },
  computed: {
    doctor() { return this.user?.doctor || null },
    lang() { return currentLang.value },
    doctorName() { return this.doctor?.full_name || this.user?.full_name || this.t('Врач') },
    initials() { return this.doctorName.split(/\s+/).slice(0, 2).map(word => word[0]).join('').toUpperCase() },
    desktopMargin() { return this.desktop ? `${this.collapsed ? 72 : 260}px` : '0px' },
    isPassesActive() { return this.$route.path.startsWith('/doctor/passes') || this.$route.path.startsWith('/doctor/surveys') },
    isHistoryActive() { return this.$route.path.startsWith('/doctor/history') }
  },
  created() {
    this.loadUser()
    window.addEventListener('resize', this.onResize)
  },
  beforeUnmount() { window.removeEventListener('resize', this.onResize) },
  methods: {
    t,
    switchLang(lang) { setLang(lang) },
    onResize() { this.desktop = window.innerWidth >= 1024 },
    toggleSidebar() { this.collapsed = !this.collapsed; localStorage.setItem('qamqor_doctor_sidebar_collapsed', String(this.collapsed)) },
    async loadUser() {
      try { const response = await api.checkAuth(); this.user = response.data.user }
      catch (_) { this.$router.replace('/login') }
    },
    async logout() { try { await api.logout() } finally { this.$router.replace('/login') } }
  }
}
</script>

<style scoped>
.sidebar-link { @apply flex items-center gap-4 rounded-[10px] px-[10px] py-[10px] text-[15px] font-light text-white/80 transition-colors hover:bg-white/10 hover:text-white; }
.sidebar-link.active { @apply bg-[#0066ff] text-white; }
.mobile-link { @apply block rounded-[10px] px-4 py-3 text-sm text-white/80 hover:bg-white/10 hover:text-white; }
.mobile-link.active { @apply bg-[#0066ff] text-white; }
</style>
